import type { ReactNode } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { RouteKey } from '@/i18n/routes'

const tones = {
  coral: 'bg-coral/15 text-coral-ink',
  sea: 'bg-sea/15 text-sea-ink',
}

type BoardingPassProps = { to: RouteKey; gate: string; title: string; text: string; icon: ReactNode; tone: keyof typeof tones }

/** Carta d'imbarco cliccabile: matrice colorata col gate, perforazione, destinazione. */
export function BoardingPass({ to, gate, title, text, icon, tone }: BoardingPassProps) {
  return (
    <LocalizedLink
      to={to}
      className="group @container relative flex flex-col overflow-hidden rounded-xl border-2 border-ink bg-paper text-ink shadow-[4px_4px_0_var(--color-ink)] transition-[transform,box-shadow] duration-300 ease-out-expo hover:-translate-y-1 hover:shadow-[6px_9px_0_var(--color-ink)] active:translate-y-0.5 active:shadow-[1px_1px_0_var(--color-ink)] active:duration-75"
    >
      <span className={cn('flex items-center justify-between gap-2 px-2.5 pt-3 pb-2.5 sm:px-5 sm:pt-4 short:px-3 short:pt-2 short:pb-1.5', tones[tone])}>
        <span className="font-body text-[0.6rem] font-semibold uppercase tracking-[0.2em] sm:text-xs">{gate}</span>
        <span aria-hidden="true" className="transition-transform duration-500 ease-out-expo group-hover:rotate-12">{icon}</span>
      </span>
      <span aria-hidden="true" className="ticket-perforation" />
      <span className="px-2.5 pt-2.5 pb-3 sm:px-5 sm:pb-4 short:px-3 short:pt-1.5 short:pb-2">
        <span className="block font-display text-[clamp(1.1rem,14cqi,2.5rem)] font-medium leading-none whitespace-nowrap tracking-tight">{title}</span>
        <span className="mt-1.5 flex items-center justify-between gap-2 text-[0.7rem] text-ink-soft sm:text-sm short:text-xs">
          {text}
          <ArrowRight aria-hidden="true" className="size-4 shrink-0 transition-transform duration-300 ease-out-expo group-hover:translate-x-1" />
        </span>
      </span>
    </LocalizedLink>
  )
}
